import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import WOW from "wow.js";
import { IconArrowLeft, IconMail, IconMapPin, IconPhone, IconShieldCheck, IconLock, IconUserCheck, IconDatabase, IconShare, IconCookie, IconEye, IconAlertCircle, IconRefresh } from "@tabler/icons-react";
import corvantaLogo from "../../Components/Images/Corvanta.png";
import "./PrivacyPolicy.scss";

const PrivacyPolicy = () => {
    useEffect(() => {
        const wow = new WOW();
        wow.init();
        // Scroll to top on mount
        window.scrollTo(0, 0);
    }, []);

    const lastUpdated = "January 13, 2026";

    const sections = [
        {
            id: "introduction",
            icon: <IconShieldCheck size={28} />,
            title: "Introduction",
            content: `Welcome to Corvanta Analytics Private Limited ("Corvanta," "we," "us," or "our"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or engage with us in any manner.

As a software development and AI solutions company, we understand the importance of data privacy and are dedicated to maintaining the trust you place in us. This policy applies to all services offered by Corvanta Analytics, including but not limited to software development, AI/ML solutions, healthcare technology, IT consulting, and all related digital services.`
        },
        {
            id: "information-collection",
            icon: <IconDatabase size={28} />,
            title: "Information We Collect",
            content: `We collect information that you provide directly to us and information that is automatically collected when you use our services.

**Personal Information You Provide:**
• Full name and contact details (email address, phone number, mailing address)
• Company name, job title, and professional information
• Project requirements and business needs
• Communication preferences and correspondence with us
• Payment and billing information for contracted services
• Resume and professional credentials (for career applications)

**Automatically Collected Information:**
• Device information (browser type, operating system, device identifiers)
• IP address and geographic location data
• Usage data (pages visited, time spent, click patterns)
• Cookies and similar tracking technologies
• Referral source and website navigation paths

**Information from Third Parties:**
• Social media profiles when you connect with us
• Business information from publicly available sources
• Analytics data from our marketing partners`
        },
        {
            id: "use-of-information",
            icon: <IconUserCheck size={28} />,
            title: "How We Use Your Information",
            content: `We use the information we collect for various business and operational purposes:

**Service Delivery:**
• To provide, maintain, and improve our software development services
• To respond to your inquiries and fulfill your requests
• To deliver project updates and technical support
• To process transactions and send related information

**Communication:**
• To send you technical notices, updates, and administrative messages
• To provide you with news, special offers, and general information about our services
• To respond to your comments, questions, and customer service requests

**Business Operations:**
• To analyze usage trends and improve user experience
• To detect, prevent, and address technical issues
• To protect against fraudulent or unauthorized activity
• To comply with legal obligations and enforce our agreements

**Marketing and Analytics:**
• To measure and analyze the effectiveness of our marketing campaigns
• To deliver targeted advertising based on your interests
• To create aggregated, anonymized data for business analysis`
        },
        {
            id: "data-sharing",
            icon: <IconShare size={28} />,
            title: "Information Sharing and Disclosure",
            content: `**We Do Not Sell Your Personal Data.**

We are committed to protecting your privacy. We do not sell, trade, or rent your personal information to third parties for their marketing purposes.

**We may share your information in the following circumstances:**

**Service Providers:**
We may share information with trusted third-party vendors who assist us in operating our website, conducting our business, or providing services to you, including:
• Cloud hosting and infrastructure providers
• Payment processors
• Analytics and marketing platforms
• Customer support tools

**Legal Requirements:**
We may disclose your information when required by law or if we believe that such disclosure is necessary to:
• Comply with legal processes, court orders, or government requests
• Protect our rights, privacy, safety, or property
• Prevent fraud or other illegal activities

**Business Transfers:**
In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction. We will notify you of any such change in ownership.

**With Your Consent:**
We may share your information with third parties when you have given us explicit consent to do so.`
        },
        {
            id: "data-security",
            icon: <IconLock size={28} />,
            title: "Data Security",
            content: `We implement robust security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.

**Security Measures Include:**
• Encryption of data in transit using SSL/TLS protocols
• Secure data storage with access controls and authentication
• Regular security assessments and vulnerability testing
• Employee training on data protection and privacy practices
• Physical security measures at our office locations
• Incident response procedures for potential data breaches

**Our Commitment:**
While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security, but we continuously work to enhance our protective measures.

**Data Retention:**
We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements.`
        },
        {
            id: "cookies",
            icon: <IconCookie size={28} />,
            title: "Cookies and Tracking Technologies",
            content: `We use cookies and similar tracking technologies to enhance your experience on our website.

**Types of Cookies We Use:**

**Essential Cookies:**
Required for the basic functionality of our website. These cookies enable core features such as security, network management, and accessibility.

**Analytics Cookies:**
Help us understand how visitors interact with our website by collecting and reporting information anonymously. We use services like Google Analytics and Segment.

**Marketing Cookies:**
Used to track visitors across websites to display relevant advertisements. These cookies may be set by our advertising partners.

**Preference Cookies:**
Allow the website to remember choices you make (such as language or region) and provide enhanced, personalized features.

**Managing Cookies:**
You can control and manage cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our website.`
        },
        {
            id: "your-rights",
            icon: <IconEye size={28} />,
            title: "Your Privacy Rights",
            content: `Depending on your location, you may have certain rights regarding your personal information:

**Access:** You have the right to request copies of your personal data that we hold.

**Rectification:** You have the right to request correction of inaccurate or incomplete personal information.

**Erasure:** You have the right to request deletion of your personal data under certain circumstances.

**Restriction:** You have the right to request that we restrict the processing of your personal data.

**Data Portability:** You have the right to request that we transfer your data to another organization or directly to you.

**Objection:** You have the right to object to our processing of your personal data for direct marketing purposes.

**Withdraw Consent:** Where we rely on consent to process your data, you have the right to withdraw that consent at any time.

**To exercise any of these rights, please contact us using the information provided below. We will respond to your request within the timeframe required by applicable law.**`
        },
        {
            id: "international-transfers",
            icon: <IconAlertCircle size={28} />,
            title: "International Data Transfers",
            content: `Corvanta Analytics operates globally with offices in India, Saudi Arabia, and the United States. Your personal information may be transferred to and processed in countries other than your country of residence.

**Our Global Presence:**
• **India:** 22, Chamiers Road, Alwarpet, Chennai-600018
• **Saudi Arabia:** 6129, Prince Mohammed bin Saad bin Abdulaziz St., Al Aqiq, Riyadh
• **United States:** Operations and support services

**Safeguards:**
When we transfer your data internationally, we implement appropriate safeguards to ensure your information is protected in accordance with this Privacy Policy. These safeguards may include:
• Standard contractual clauses approved by relevant authorities
• Binding corporate rules
• Compliance with applicable data protection frameworks

**Compliance:**
We comply with applicable data protection laws, including the General Data Protection Regulation (GDPR) for EU residents, the California Consumer Privacy Act (CCPA) for California residents, and India's Digital Personal Data Protection Act.`
        },
        {
            id: "updates",
            icon: <IconRefresh size={28} />,
            title: "Changes to This Privacy Policy",
            content: `We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.

**Notification of Changes:**
• We will post the updated Privacy Policy on this page
• We will update the "Last Updated" date at the top of this policy
• For significant changes, we may provide additional notice (such as email notification or a prominent notice on our website)

**Your Continued Use:**
Your continued use of our services after any changes to this Privacy Policy constitutes your acceptance of the updated policy. We encourage you to periodically review this page for the latest information on our privacy practices.

**Version History:**
We maintain records of previous versions of this Privacy Policy and can provide them upon request.`
        }
    ];

    return (
        <div className="privacy-policy-page">
            {/* Header */}
            <header className="privacy-header">
                <div className="header-content">
                    <Link to="/" className="logo-link">
                        <img src={corvantaLogo} alt="Corvanta Analytics" className="logo" />
                    </Link>
                    <Link to="/" className="back-button">
                        <IconArrowLeft size={20} />
                        <span>Back to Home</span>
                    </Link>
                </div>
            </header>

            {/* Hero Section */}
            <section className="privacy-hero wow fadeInUp">
                <div className="hero-content">
                    <div className="hero-badge">
                        <IconShieldCheck size={24} />
                        <span>Privacy First</span>
                    </div>
                    <h1 className="hero-title">
                        <span className="gradient-text">Privacy</span> Policy
                    </h1>
                    <p className="hero-subtitle">
                        Your trust is our foundation. Learn how we protect and respect your data.
                    </p>
                    <div className="last-updated">
                        Last Updated: <strong>{lastUpdated}</strong>
                    </div>
                </div>
                <div className="hero-decoration">
                    <div className="decoration-circle circle-1"></div>
                    <div className="decoration-circle circle-2"></div>
                    <div className="decoration-circle circle-3"></div>
                </div>
            </section>

            {/* Quick Summary */}
            <section className="quick-summary wow fadeInUp">
                <div className="summary-container">
                    <h2>Key Points at a Glance</h2>
                    <div className="summary-grid">
                        <div className="summary-card">
                            <div className="card-icon">
                                <IconLock size={32} />
                            </div>
                            <h3>We Don't Sell Data</h3>
                            <p>Your personal information is never sold to third parties for marketing purposes.</p>
                        </div>
                        <div className="summary-card">
                            <div className="card-icon">
                                <IconShieldCheck size={32} />
                            </div>
                            <h3>Enterprise Security</h3>
                            <p>We implement industry-standard security measures to protect your data.</p>
                        </div>
                        <div className="summary-card">
                            <div className="card-icon">
                                <IconUserCheck size={32} />
                            </div>
                            <h3>Your Rights Matter</h3>
                            <p>Access, correct, or delete your personal information at any time.</p>
                        </div>
                        <div className="summary-card">
                            <div className="card-icon">
                                <IconDatabase size={32} />
                            </div>
                            <h3>Transparent Practices</h3>
                            <p>We clearly explain what data we collect and how we use it.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Table of Contents */}
            <section className="table-of-contents wow fadeInUp">
                <div className="toc-container">
                    <h2>Table of Contents</h2>
                    <nav className="toc-nav">
                        {sections.map((section, index) => (
                            <a key={section.id} href={`#${section.id}`} className="toc-link">
                                <span className="toc-number">{String(index + 1).padStart(2, '0')}</span>
                                <span className="toc-title">{section.title}</span>
                            </a>
                        ))}
                    </nav>
                </div>
            </section>

            {/* Policy Sections */}
            <main className="policy-content">
                {sections.map((section, index) => (
                    <section
                        key={section.id}
                        id={section.id}
                        className="policy-section wow fadeInUp"
                    >
                        <div className="section-container">
                            <div className="section-header">
                                <div className="section-icon">{section.icon}</div>
                                <div className="section-number">{String(index + 1).padStart(2, '0')}</div>
                                <h2>{section.title}</h2>
                            </div>
                            <div className="section-content">
                                {section.content.split('\n\n').map((paragraph, pIndex) => (
                                    <p key={pIndex} dangerouslySetInnerHTML={{
                                        __html: paragraph
                                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                            .replace(/•/g, '<span class="bullet">•</span>')
                                            .replace(/\n/g, '<br />')
                                    }} />
                                ))}
                            </div>
                        </div>
                    </section>
                ))}
            </main>

            {/* Contact Section */}
            <section className="contact-section wow fadeInUp">
                <div className="contact-container">
                    <h2>Contact Us About Privacy</h2>
                    <p className="contact-intro">
                        If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices,
                        please don't hesitate to contact us.
                    </p>
                    <div className="contact-cards">
                        <div className="contact-card">
                            <div className="contact-icon">
                                <IconMail size={28} />
                            </div>
                            <h3>Email</h3>
                            <a href="mailto:Contactus@corvanta.io">Contactus@corvanta.io</a>
                            <p>For privacy-related inquiries</p>
                        </div>
                        <div className="contact-card">
                            <div className="contact-icon">
                                <IconPhone size={28} />
                            </div>
                            <h3>Phone</h3>
                            <div className="phone-numbers">
                                <a href="tel:+919003079382">India: +91 9003079382</a>
                                <a href="tel:+966580028002">KSA: +966 58 002 8002</a>
                                <a href="tel:+13322569512">USA: +1 (332) 256 9512</a>
                            </div>
                        </div>
                        <div className="contact-card">
                            <div className="contact-icon">
                                <IconMapPin size={28} />
                            </div>
                            <h3>Head Office</h3>
                            <address>
                                Corvanta Analytics Private Limited<br />
                                22, Chamiers Road, Alwarpet<br />
                                Chennai - 600018, India
                            </address>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="privacy-footer">
                <div className="footer-content">
                    <div className="footer-left">
                        <img src={corvantaLogo} alt="Corvanta Analytics" className="footer-logo" />
                        <p>© {new Date().getFullYear()} Corvanta Analytics Private Limited. All rights reserved.</p>
                    </div>
                    <div className="footer-links">
                        <Link to="/">Home</Link>
                        <Link to="/privacy-policy" className="active">Privacy Policy</Link>
                        <a href="mailto:Contactus@corvanta.io">Contact</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
